import { gql } from "@apollo/client";
import { useSubscription } from "@apollo/client/react";
import { useEffect, useRef } from "react";
import type {
  CanonicalPinnedTaskIdsQuery,
  ProfilePinnedTaskIdsFragment,
  ProfileUpdatedSubscription,
} from "../graphql/types";

const PROFILE_UPDATED_FRAGMENT = gql`
  fragment ProfileUpdatedFields on User {
    id
    firstName
    lastName
    email
    appLanguage
    transcriptionLanguage
    pushNotifications
    updatedAt
    licenseType
    licenseTrial
    pinnedTaskIds
    pendings {
      groups
    }
  }
`;

const PROFILE_PINNED_TASK_IDS_FRAGMENT = gql`
  fragment ProfilePinnedTaskIds on User {
    pinnedTaskIds
  }
`;

const SUBSCRIPTION_PROFILE_UPDATED = gql`
  subscription ProfileUpdated {
    profileUpdated {
      ...ProfileUpdatedFields
    }
  }
  ${PROFILE_UPDATED_FRAGMENT}
`;

const GET_CANONICAL_PINNED_TASK_IDS = gql`
  query CanonicalPinnedTaskIds {
    user {
      id
      pinnedTaskIds
    }
  }
`;

const hasSamePinnedTaskIds = (
  current: readonly string[] | null | undefined,
  updated: readonly string[] | null | undefined,
) =>
  (current?.length ?? 0) === (updated?.length ?? 0) &&
  (current ?? []).every((taskId, index) => taskId === updated?.[index]);

export const useProfileSubscriptions = () => {
  const reconciliationVersionRef = useRef(0);
  const latestReconciliationPendingRef = useRef(false);
  const reconciliationRequiredRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      reconciliationVersionRef.current += 1;
      latestReconciliationPendingRef.current = false;
    };
  }, []);

  useSubscription<ProfileUpdatedSubscription>(SUBSCRIPTION_PROFILE_UPDATED, {
    onData: ({ client, data }) => {
      const profileUpdated = data?.data?.profileUpdated;

      if (!profileUpdated) {
        return;
      }

      const reconciliationVersion = ++reconciliationVersionRef.current;
      const userCacheId = client.cache.identify({
        __typename: "User",
        id: profileUpdated.id,
      });

      if (!userCacheId) {
        return;
      }

      const currentPinnedTaskIds =
        client.cache.readFragment<ProfilePinnedTaskIdsFragment>({
          id: userCacheId,
          fragment: PROFILE_PINNED_TASK_IDS_FRAGMENT,
          optimistic: true,
        })?.pinnedTaskIds;
      const requiresReconciliation = !hasSamePinnedTaskIds(
        currentPinnedTaskIds,
        profileUpdated.pinnedTaskIds,
      );
      const shouldReconcile =
        requiresReconciliation ||
        latestReconciliationPendingRef.current ||
        reconciliationRequiredRef.current;

      client.cache.writeFragment({
        id: userCacheId,
        fragment: PROFILE_UPDATED_FRAGMENT,
        data: profileUpdated,
      });

      if (!shouldReconcile) {
        return;
      }

      latestReconciliationPendingRef.current = true;
      reconciliationRequiredRef.current = false;

      const hasExpectedCachedOrder = () =>
        hasSamePinnedTaskIds(
          client.cache.readFragment<ProfilePinnedTaskIdsFragment>({
            id: userCacheId,
            fragment: PROFILE_PINNED_TASK_IDS_FRAGMENT,
            optimistic: true,
          })?.pinnedTaskIds,
          profileUpdated.pinnedTaskIds,
        );

      const reconcile = async (attempt = 0): Promise<void> => {
        if (
          !mountedRef.current ||
          reconciliationVersion !== reconciliationVersionRef.current ||
          !hasExpectedCachedOrder()
        ) {
          return;
        }

        try {
          const { data: canonicalData } =
            await client.query<CanonicalPinnedTaskIdsQuery>({
              query: GET_CANONICAL_PINNED_TASK_IDS,
              fetchPolicy: "no-cache",
              context: { queryDeduplication: false },
            });
          const canonicalUser = canonicalData?.user;

          if (
            !mountedRef.current ||
            reconciliationVersion !== reconciliationVersionRef.current ||
            !hasExpectedCachedOrder()
          ) {
            return;
          }

          const canonicalUserCacheId = canonicalUser
            ? client.cache.identify({
                __typename: "User",
                id: canonicalUser.id,
              })
            : undefined;

          if (!canonicalUser || !canonicalUserCacheId) {
            if (attempt === 0) {
              await reconcile(1);
            } else {
              reconciliationRequiredRef.current = true;
            }
            return;
          }

          client.cache.modify({
            id: canonicalUserCacheId,
            fields: {
              pinnedTaskIds: () => canonicalUser.pinnedTaskIds,
            },
          });
          reconciliationRequiredRef.current = false;
        } catch {
          if (attempt === 0) {
            await reconcile(1);
          } else if (
            mountedRef.current &&
            reconciliationVersion === reconciliationVersionRef.current
          ) {
            reconciliationRequiredRef.current = true;
          }
        }
      };

      void reconcile().finally(() => {
        if (reconciliationVersion === reconciliationVersionRef.current) {
          latestReconciliationPendingRef.current = false;
        }
      });
    },
  });
};
